class Theme14PlusInfiniteScroll {
  constructor(container) {
    this.container = container;
    this.loading = false;
    this.observer = null;
    this.itemsSelector = '';
    this.refresh();
  }

  refresh() {
    this.grid = this.resolveItemsContainer();
    this.paginationWrapper = this.container.querySelector('.pagination-wrapper');
    this.nextLink = this.paginationWrapper?.querySelector('[data-pagination-next]') || null;
    this.container.classList.toggle('is-infinite-ready', Boolean(this.nextLink));

    if (this.observer) {
      this.observer.disconnect();
    }

    if (!this.nextLink || !this.paginationWrapper || !this.grid) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.loadNextPage();
          }
        });
      },
      {
        rootMargin: '240px 0px',
      }
    );

    this.observer.observe(this.paginationWrapper);
  }

  async loadNextPage() {
    if (this.loading || !this.nextLink || !this.grid) return;

    this.loading = true;
    this.container.classList.add('is-loading-next-page');

    try {
      const nextUrl = this.nextLink.href;
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = this.itemsSelector ? parsedDocument.querySelector(this.itemsSelector) : null;
      const nextPaginationWrapper = parsedDocument.querySelector('.pagination-wrapper');
      const nextItems = nextGrid ? Array.from(nextGrid.children) : [];

      nextItems.forEach((item) => {
        this.grid.appendChild(item);
      });

      if (this.paginationWrapper) {
        if (nextPaginationWrapper) {
          this.paginationWrapper.replaceWith(nextPaginationWrapper);
        } else {
          this.paginationWrapper.remove();
        }
      }

      history.replaceState(history.state, '', nextUrl);
      this.container.dispatchEvent(
        new CustomEvent('theme14plus:page-appended', {
          bubbles: true,
          detail: { url: nextUrl },
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
      this.container.classList.remove('is-loading-next-page');
      this.refresh();
    }
  }

  resolveItemsContainer() {
    const customItemsContainer = this.container.querySelector('[data-theme14plus-infinite-scroll-items]');

    if (customItemsContainer) {
      const customIdentifier = customItemsContainer.dataset.theme14plusInfiniteScrollItems;
      this.itemsSelector = customIdentifier
        ? `[data-theme14plus-infinite-scroll-items="${customIdentifier}"]`
        : '[data-theme14plus-infinite-scroll-items]';
      return customItemsContainer;
    }

    const productGrid = this.container.querySelector('#product-grid');

    if (productGrid) {
      this.itemsSelector = '#product-grid';
      return productGrid;
    }

    this.itemsSelector = '';
    return null;
  }
}

function initializeInfiniteScroll() {
  document.querySelectorAll('.js-theme14plus-infinite-scroll').forEach((container) => {
    if (container.theme14plusInfiniteScroll) {
      container.theme14plusInfiniteScroll.refresh();
      return;
    }

    container.theme14plusInfiniteScroll = new Theme14PlusInfiniteScroll(container);
  });
}

function initializeProductTabs() {
  document.querySelectorAll('.js-theme14plus-tabs').forEach((tabRoot) => {
    if (tabRoot.dataset.initialized === 'true') return;

    const tabButtons = Array.from(tabRoot.querySelectorAll('[data-theme14plus-tab]'));
    const tabPanels = Array.from(tabRoot.querySelectorAll('[data-theme14plus-panel]'));

    if (!tabButtons.length || !tabPanels.length) return;

    const activateTab = (targetId) => {
      tabButtons.forEach((button) => {
        const isActive = button.dataset.theme14plusTab === targetId;
        button.setAttribute('aria-selected', String(isActive));
        button.classList.toggle('is-active', isActive);
        button.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      tabPanels.forEach((panel) => {
        const isActive = panel.dataset.theme14plusPanel === targetId;
        panel.hidden = !isActive;
      });
    };

    tabButtons.forEach((button) => {
      button.addEventListener('click', () => activateTab(button.dataset.theme14plusTab));
    });

    activateTab(tabButtons[0].dataset.theme14plusTab);
    tabRoot.dataset.initialized = 'true';
  });
}

function initializeCartDeliveryDate(root = document) {
  root.querySelectorAll('[data-theme14plus-delivery-date]').forEach((select) => {
    const selectedValue = select.dataset.selectedValue || '';
    const daysFromToday = Number(select.dataset.daysStart || 5);
    const rangeDays = Number(select.dataset.daysCount || 10);
    const label = select.dataset.shortestLabel || '最短でのお届け';

    const formatter = new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Tokyo',
    });

    const today = new Date();
    const options = [`<option value="">${label}</option>`];

    for (let index = 0; index < rangeDays; index += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + daysFromToday + index);
      const value = formatter.format(date).replaceAll('-', '/');
      options.push(`<option value="${value}">${value}</option>`);
    }

    const renderedOptions = options.join('');

    if (select.dataset.theme14plusDeliveryOptions === renderedOptions) {
      if (selectedValue) {
        select.value = selectedValue;
      }
      return;
    }

    select.innerHTML = renderedOptions;
    select.dataset.theme14plusDeliveryOptions = renderedOptions;

    if (selectedValue) {
      select.value = selectedValue;
    }
  });
}

function initializeTheme14Plus() {
  initializeInfiniteScroll();
  initializeProductTabs();
  initializeCartDeliveryDate();
}

document.addEventListener('DOMContentLoaded', initializeTheme14Plus);
document.addEventListener('theme14plus:product-grid-updated', initializeInfiniteScroll);
document.addEventListener('shopify:section:load', initializeTheme14Plus);

document.addEventListener('DOMContentLoaded', () => {
  const cartFooter = document.getElementById('main-cart-footer');

  if (!cartFooter) return;

  const observer = new MutationObserver((mutations) => {
    const shouldInitialize = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;

        return (
          node.matches('[data-theme14plus-delivery-date]') ||
          Boolean(node.querySelector('[data-theme14plus-delivery-date]'))
        );
      });
    });

    if (!shouldInitialize) return;

    initializeCartDeliveryDate(cartFooter);
  });

  observer.observe(cartFooter, { childList: true, subtree: true });
});
